/**
 * Public Status Page — no auth required, reachable at /status.
 *
 * Customer-friendly real-time view of the services that real users
 * care about (Apple Wallet, Google Wallet, OTP SMS, Email, Sito sicuro).
 * Designed to be the link LoJack Customer Service / Mara share with
 * dealers when something looks broken, so they get a definitive
 * "Operativo" / "Interruzione in corso" answer instead of opening a
 * ticket.
 *
 * Visual language is intentionally minimal: institutional blue brand,
 * one big "stoplight" headline at the top, then a flat list of the
 * 6 customer-facing services with a coloured dot each.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, CircleAlert, RefreshCw, ExternalLink } from "lucide-react";
import { API, LOJACK_LOGO } from "../config";

const STATUS_META = {
  operational: {
    label: "Operativo",
    dotClass: "bg-lojack-blue-500",
    icon: CheckCircle2,
    iconClass: "text-lojack-blue-400",
    rowBg: "bg-[#0d0d0d]",
    rowBorder: "border-gray-800/70",
  },
  degraded: {
    label: "Funzionante con limitazioni",
    dotClass: "bg-lojack-clause-500",
    icon: AlertTriangle,
    iconClass: "text-lojack-clause-400",
    rowBg: "bg-lojack-clause-500/5",
    rowBorder: "border-lojack-clause-500/30",
  },
  outage: {
    label: "Interruzione in corso",
    dotClass: "bg-red-500",
    icon: CircleAlert,
    iconClass: "text-red-400",
    rowBg: "bg-red-500/5",
    rowBorder: "border-red-500/30",
  },
};

const OVERALL_META = {
  operational: {
    label: "Tutti i sistemi operativi",
    accent: "lojack-blue",
    chipBg: "bg-lojack-blue-500/15",
    chipBorder: "border-lojack-blue-500/40",
    chipText: "text-lojack-blue-300",
    bigText: "text-lojack-blue-300",
  },
  degraded: {
    label: "Servizi con prestazioni ridotte",
    accent: "amber",
    chipBg: "bg-lojack-clause-500/15",
    chipBorder: "border-lojack-clause-500/40",
    chipText: "text-lojack-clause-300",
    bigText: "text-lojack-clause-300",
  },
  outage: {
    label: "Interruzione in corso",
    accent: "red",
    chipBg: "bg-red-500/15",
    chipBorder: "border-red-500/40",
    chipText: "text-red-300",
    bigText: "text-red-300",
  },
};

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: "Europe/Rome", timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};


const PublicStatusPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setError(null);
      const r = await axios.get(`${API}/public/status`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Errore di rete");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 60s — matches the Cache-Control max-age on the API
    const interval = setInterval(load, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const overall = OVERALL_META[data?.overall_status] || OVERALL_META.operational;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="public-status-page">
      {/* Brand header */}
      <header className="border-b border-gray-900 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {LOJACK_LOGO && (
              <img src={LOJACK_LOGO} alt="LoJack" className="h-8 w-auto" />
            )}
            <div>
              <p className="text-white font-bold text-sm tracking-tight">LoJack Wallet</p>
              <p className="text-gray-500 text-[11px]">Status Page</p>
            </div>
          </div>
          <a
            href="https://securewallet.lojack.eu"
            className="text-gray-500 hover:text-lojack-blue-400 text-xs flex items-center gap-1.5"
            data-testid="status-back-link"
          >
            Vai al portale <ExternalLink size={11} />
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {loading && !data && (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={20} className="animate-spin text-gray-500" />
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-300 rounded-xl p-4 mb-6 text-sm">
            Impossibile contattare il sistema di status. {error}
          </div>
        )}

        {data && (
          <>
            {/* Big stoplight headline */}
            <div className={`rounded-2xl border ${overall.chipBorder} ${overall.chipBg} p-6 mb-6`} data-testid="status-overall">
              <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${overall.chipText} opacity-80`}>
                Stato attuale
              </p>
              <h1 className={`text-3xl sm:text-4xl font-bold mt-2 ${overall.bigText}`}>
                {data.headline || overall.label}
              </h1>
              <p className="text-gray-400 text-xs mt-3 font-mono">
                Aggiornato: {formatTimestamp(data.generated_at)}
              </p>
            </div>

            {/* Service list */}
            <div className="space-y-2.5">
              {data.services.map((svc) => {
                const meta = STATUS_META[svc.status] || STATUS_META.operational;
                const Icon = meta.icon;
                return (
                  <div
                    key={svc.name}
                    className={`rounded-xl border ${meta.rowBorder} ${meta.rowBg} p-4 flex items-start gap-3`}
                    data-testid={`status-row-${svc.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${meta.dotClass} mt-1.5 shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-white font-semibold text-sm">{svc.name}</p>
                        <span className={`text-xs font-medium flex items-center gap-1 ${meta.iconClass}`}>
                          <Icon size={12} />
                          {svc.status_label || meta.label}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">{svc.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Refresh hint */}
            <p className="text-gray-600 text-[11px] text-center mt-10 leading-relaxed">
              La pagina si aggiorna automaticamente ogni minuto.<br />
              In caso di disservizio, il team LoJack viene allertato in tempo reale via email.
              Per supporto contatta il tuo dealer di riferimento o il servizio clienti LoJack.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default PublicStatusPage;
